import React, { forwardRef, useContext, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton } from 'react-share';

import CloseIcon from '../../assets/icons/CloseIcon';
import FacebookIcon from '../../assets/icons/FacebookIcon';
import LinkedInIcon from '../../assets/icons/LinkedInIcon';
import MicrosoftTeamsIcon from '../../assets/icons/MicrosoftTeamsIcon';
import TwitterIcon from '../../assets/icons/TwitterIcon';
import { TenantCtx } from '../../containers/CommonLayout/CommonLayout';
import { colorsPalette } from '../../theme';
import { TenantsRefs } from '../../utils/const';
import { getCourseSocialShareImage, getSocialShareImage, translate } from '../../utils/helpers';
import useShareTeams from '../../utils/hooks/useShareTeams';
import ImageWithLoader from '../ImageWithLoader/ImageWithLoader';
import { CardWrapper } from '../UI/CardWrapper';
import { ShareButton } from '../UI/ShareButton';
import useStyles from './styles';

export enum ShareType {
  Pathway = 'pathway',
  Course = 'course',
}

type GenerateShareDataInput = {
  type: ShareType;
  resultHash?: string | null;
  tenantName?: string | null;
  entityName?: string | null;
  cpdCredits?: string | null;
  category?: string | null;
  fbName?: string | null;
  twitterHandle?: string | null;
};

const generateShareData = ({
  type,
  resultHash,
  tenantName,
  entityName,
  cpdCredits,
  category,
  fbName,
  twitterHandle,
}: GenerateShareDataInput) => {
  const defaultShareText =
    tenantName === TenantsRefs.Fu
      ? translate('page.pathway.pathwayShareFu')
      : tenantName === TenantsRefs.Su
      ? translate('page.pathway.pathwayShareSu')
      : undefined;

  switch (type) {
    case ShareType.Pathway: {
      const shareText =
        entityName && cpdCredits
          ? translate('page.test.shareText', { pathwayName: entityName, cpdCredits })
          : defaultShareText;
      const shareTwitterText =
        entityName && category
          ? translate('page.test.shareTwitterText', {
              pathwayName: entityName,
              twitterHandle,
              category,
            })
          : defaultShareText;
      const shareFacebookText =
        entityName && category
          ? translate('page.test.shareFacebookText', { pathwayName: entityName, fbName, category })
          : defaultShareText;

      return {
        shareLink: `${window?.location?.origin}/share/${resultHash}`,
        socialShareImgUrl: getSocialShareImage(resultHash, tenantName),
        shareText,
        shareTwitterText,
        shareFacebookText,
      };
    }
    case ShareType.Course: {
      const shareText = entityName
        ? translate('page.course.shareText', { courseName: entityName })
        : '';
      const shareTwitterText = entityName
        ? translate('page.course.shareTwitterText', {
            courseName: entityName,
            twitterHandle,
            category,
          })
        : '';
      const shareFacebookText = entityName
        ? translate('page.course.shareFacebookText', { courseName: entityName, fbName })
        : '';

      return {
        shareLink: `${window?.location?.origin}/share/courses/${resultHash}`,
        socialShareImgUrl: getCourseSocialShareImage(resultHash, tenantName),
        shareText,
        shareTwitterText,
        shareFacebookText,
      };
    }
  }
};

const formatHashTags = (hashtags?: (string | null)[] | null): string[] => {
  const arr = (hashtags ? hashtags.filter((hashtag) => Boolean(hashtag)) : []) as string[];

  return arr.map((hashtag) => (hashtag?.startsWith('#') ? hashtag?.substring(1) : hashtag));
};

interface ShareModalProps {
  closeModal?: () => void;
  resultHash?: string | null;
  entityName?: string | null;
  cpdCredits?: string | null;
  category?: string | null;
  type?: ShareType;
}

const ShareModal = forwardRef<HTMLDivElement, ShareModalProps>(
  ({ closeModal, resultHash, entityName, cpdCredits, category, type = ShareType.Pathway }, ref) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { tenant } = useContext(TenantCtx);

    const tenantName = tenant?.name;
    const fbName = tenant?.fb_name;
    const twitterHandle = tenant?.twitter_handle;
    const twitterHashtags = tenant?.twitter_hashtags_array;

    const { handleTeamsShare, teamsShareRef } = useShareTeams();
    const formattedHashtags = useMemo(() => formatHashTags(twitterHashtags), [twitterHashtags]);

    const { shareLink, socialShareImgUrl, shareText, shareFacebookText, shareTwitterText } =
      useMemo(
        () =>
          generateShareData({
            type,
            resultHash,
            tenantName,
            cpdCredits,
            category,
            entityName,
            fbName,
            twitterHandle,
          }),
        [type, resultHash, tenantName, entityName, category, cpdCredits, fbName, twitterHandle],
      );

    return (
      <CardWrapper ref={ref} wrapperClass={classes.wrapper} data-testid="shareModal">
        <Box className={classes.headerWrapper}>
          <Typography variant="h2" className={classes.title}>
            {t('page.test.shareYourSuccess')}
          </Typography>
          <CloseIcon
            data-testid="shareModalClose"
            color={colorsPalette.gray}
            className={classes.closeIcon}
            onClick={closeModal}
          />
        </Box>
        <Box className={classes.content}>
          <div className={classes.certificateImgWrapper}>
            <ImageWithLoader
              ratio={52}
              imgWrapperClass={classes.certificateImgWrapperWithBg}
              className={classes.certificateImg}
              path={socialShareImgUrl}
              alt={t('page.test.share')}
              srcset={[{ width: 750 }]}
              sizes="100vw"
              data-testid="certificateImg"
            />
          </div>
          <Box className={classes.buttonsWrapper}>
            <LinkedinShareButton
              className={classes.shareButtonWrapper}
              title={shareText}
              url={shareLink}
              resetButtonStyle={true}
              data-testid="linkedinShare"
            >
              <ShareButton wrapperClass={classes.shareButton}>
                <LinkedInIcon color={colorsPalette.white} />
                {t('page.test.linkedIn')}
              </ShareButton>
            </LinkedinShareButton>
            <ShareButton
              data-testid="shareMSTeams"
              wrapperClass={classes.shareButton}
              onClick={handleTeamsShare}
            >
              <MicrosoftTeamsIcon color={colorsPalette.white} />
              {t('page.test.microsoftTeams')}
            </ShareButton>
            <FacebookShareButton
              data-testid="facebookShare"
              quote={shareFacebookText}
              url={shareLink}
              resetButtonStyle={true}
            >
              <ShareButton wrapperClass={classes.shareButton}>
                <FacebookIcon color={colorsPalette.white} />
                {t('page.test.facebook')}
              </ShareButton>
            </FacebookShareButton>
            <TwitterShareButton
              data-testid="twitterShare"
              className={classes.shareButtonWrapper}
              title={shareTwitterText}
              url={shareLink}
              resetButtonStyle={true}
              hashtags={formattedHashtags}
            >
              <ShareButton wrapperClass={classes.shareButton}>
                <TwitterIcon color={colorsPalette.white} />
                {t('page.test.twitter')}
              </ShareButton>
            </TwitterShareButton>
            <div
              className={cx('teams-share-button', classes.teamsShareOriginal)}
              data-href={shareLink}
              data-msg-text={shareText}
              ref={teamsShareRef}
            />
          </Box>
        </Box>
      </CardWrapper>
    );
  },
);

export default ShareModal;
